<template>
<div>
  <validation-provider
    #default="validationContext"
    :name="$tc('types_alarms.title', 2)"
    rules="required"
  >
    <b-form-group
      class="text-capitalize"
      :label="$tc('types_alarms.title', 2)"
      label-for="id_type_alarm"
      :state="getValidationState(validationContext)"
    >
      <!-- Seleccionar todos o solo coincidencias -->
      <div
        class="d-flex align-items-center justify-content-between mb-1 position-relative"
      >
        <b-form-checkbox
          :checked="allVisibleSelected"
          :indeterminate="someVisibleSelected"
          :disabled="!visibleTypeAlarmIds.length"
          data-cy="allPermissions"
          @change="toggleVisibleOptions"
        >
          <span class="text-capitalize">
            {{ selectAllLabel }}
          </span>
          <span class="text-capitalize pl-2">
            {{ $t("alarm.selected_alarms") }} ({{
              itemData.type_alarm ? itemData.type_alarm.length : 0
            }})
          </span>
        </b-form-checkbox>

        <small
          v-if="searchText"
          class="text-muted ml-1"
        >
          {{ visibleTypeAlarmOptions.length }}
          coincidencia{{
            visibleTypeAlarmOptions.length === 1 ? "" : "s"
          }}
        </small>

        <!-- Category filter button -->
        <span
          v-if="!searchText"
          @click="toggleModal"
          style="cursor: pointer; padding: 5px;"
        >
          <b-icon-filter-circle-fill
            scale="1.5"
            class="text-primary"
          />
        </span>
      </div>

      <v-select
        :value="itemData.type_alarm"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        :options="normalizedTypeAlarmOptions"
        :reduce="option => option.value"
        :clearable="false"
        :close-on-select="false"
        :clear-search-on-select="false"
        :hide-selected="false"
        :deselect-from-dropdown="true"
        :filter="filterTypeAlarmOptions"
        input-id="id_type_alarm"
        multiple
        data-cy="automatest-wizard-type-alarm-select"
        @input="onTypeAlarmInput"
        @search="onTypeAlarmSearch"
      >
        <template #option="option">
          <div
            class="type-alarm-option d-flex align-items-center"
          >
            <span
              class="custom-option-checkbox mr-50"
              :class="{
                'custom-option-checkbox-selected':
                  isTypeAlarmSelected(option.value),
              }"
            >
              <feather-icon
                v-if="isTypeAlarmSelected(option.value)"
                icon="CheckIcon"
                size="13"
              />
            </span>

            <span>
              {{ option.label }}
            </span>
          </div>
        </template>

        <template #no-options>
          {{ $t("alarm.type_alarm_not_found") }}
        </template>
      </v-select>

      <b-form-invalid-feedback
        :state="getValidationState(validationContext)"
      >
        {{ validationContext.errors[0] }}
      </b-form-invalid-feedback>
    </b-form-group>
  </validation-provider>

  <!-- Modal filters -->
  <b-modal id="type-alarm-category-filters-modal" no-close-on-esc no-close-on-backdrop>
      <template #modal-title>
        <h5 class="modal-title text-capitalize text-white">
          {{ $t("alarm.filters") }}
        </h5>
      </template>
      <b-row>
        <b-col cols="7" class="my-1 mb-md-0">
          <b-form-group
            class="text-capitalize"
            :label="$t('alarm.type_alarm_categories')"
            label-for="id_profiles"
          >
            <v-select
              :options="typeAlarmCategoryOptions"
              :close-on-select="true"
              input-id="id_profiles"
              :placeholder="$t('alarm.type_alarm_categories_placeholder')"
              @input="onChangeCategory"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <template #modal-footer="{ close }">
        <div class="w-100 rfm-footer">
          <div class="d-flex flex-row align-items-stretch justify-content-between">
            <b-button
              variant="danger"
              class="mr-2 mb-0"
              @click="close"
            >
              {{ $t("actions.cancel") }}
            </b-button>
            <div>
              <b-button
                variant="outline-primary"
                class="mr-2 mb-0"
                @click="onSelectFiltered(false)"
                :disabled="!idTypeAlarmsFiltered.length"
              >
                {{ $t('alarm.remove_options') }} ({{ idTypeAlarmsFiltered.length }})
              </b-button>
              <b-button
                variant="primary"
                class="mr-2 mb-0"
                @click="onSelectFiltered(true)"
                :disabled="!idTypeAlarmsFiltered.length"
              >
                {{ $t('alarm.select_options') }} ({{ idTypeAlarmsFiltered.length }})
              </b-button>
            </div>
          </div>
        </div>
      </template>
  </b-modal>
</div>
</template>

<script>
import {
  BFormGroup,
  BFormInvalidFeedback,
  BFormCheckbox,
} from "bootstrap-vue";

import {
  ref,
  computed,
  getCurrentInstance,
  onMounted,
} from "@vue/composition-api";

import {
  ValidationProvider,
} from "vee-validate";

import formValidation from "@core/comp-functions/forms/form-validation";
import vSelect from "vue-select";
import store from "@/store";

export default {
  name: "MultipleAlarmsSelect",

  components: {
    ValidationProvider,
    BFormGroup,
    BFormInvalidFeedback,
    BFormCheckbox,
    vSelect,
  },

  props: {
    itemData: {
      type: Object,
      required: true,
      default: () => ({
        type_alarm: [],
      }),
    },
    options: {
      type: Array,
      required: true,
    },
  },

  setup(props, { root, emit }) {
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;

    const idTypeAlarmsFiltered = ref([]);

    //Saves text that is wrote in the search
    const searchText = ref("");

    const {
      getValidationState,
    } = formValidation();


    //converts text to a comprable example "camara" can find "Cámara".
    const normalizeSearchText = value => {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
    };

    /*
     * Normalize options
     *
     * allows:
     * { value, label }
     * { id, name }
     * { id, description }
     */

    const normalizedTypeAlarmOptions =
      computed(() => {
        return (props.options || [])
          .map(option => {
            if (
              option === null ||
              option === undefined
            ) {
              return null;
            }

            if (
              typeof option === "number" ||
              typeof option === "string"
            ) {
              return {
                value: option,
                label: String(option),
              };
            }

            const value =
              option.value ??
              option.id ??
              null;

            if (
              value === null ||
              value === undefined
            ) {
              return null;
            }

            return {
              ...option,

              value,

              label:
                option.label ||
                option.name ||
                option.description ||
                `#${value}`,
            };
          })
          .filter(Boolean);
      });


    //filter
    const matchesTypeAlarmSearch = option => {
      const search =
        normalizeSearchText(
          searchText.value
        );

      if (!search) {
        return true;
      }

      const searchableFields = [
        option?.label,
        option?.name,
        option?.description,
        option?.value,
      ];

      return searchableFields.some(field =>
        normalizeSearchText(field).includes(
          search
        )
      );
    };

    //Function that vue-slect uses to show options: -options and -search
    const filterTypeAlarmOptions = (
      options,
      search
    ) => {
      const normalizedSearch =
        normalizeSearchText(search);

      if (!normalizedSearch) {
        return options;
      }

      return options.filter(option => {
        const searchableFields = [
          option?.label,
          option?.name,
          option?.description,
          option?.value,
        ];

        return searchableFields.some(field =>
          normalizeSearchText(field).includes(
            normalizedSearch
          )
        );
      });
    };


    //options that does not match with current selection,  if there is no seach it cointains all options
    const visibleTypeAlarmOptions =
      computed(() => {
        return normalizedTypeAlarmOptions.value
          .filter(matchesTypeAlarmSearch);
      });


    //Visible Option Ids with out repeating
    const visibleTypeAlarmIds =
      computed(() => {
        const uniqueIds = new Map();

        visibleTypeAlarmOptions.value.forEach(
          option => {
            uniqueIds.set(
              String(option.value),
              option.value
            );
          }
        );

        return [
          ...uniqueIds.values(),
        ];
      });

    //current selected ids
    const selectedTypeAlarmIds =
      computed(() => {
        return Array.isArray(
          props.itemData?.type_alarm
        )
          ? props.itemData.type_alarm
        : [];
      });

    const isTypeAlarmSelected = value => {
      return selectedTypeAlarmIds.value.some(
        selectedValue =>
          String(selectedValue) ===
          String(value)
      );
    };

    //True when all visible options are selected
    const allVisibleSelected =
      computed(() => {
        if (
          !visibleTypeAlarmIds.value.length
        ) {
          return false;
        }

        return visibleTypeAlarmIds.value.every(
          value =>
            isTypeAlarmSelected(value)
        );
      });


    //Intermediate state, just some coincidence are selected but not alla
    const someVisibleSelected =
      computed(() => {
        if (
          !visibleTypeAlarmIds.value.length
        ) {
          return false;
        }

        const selectedCount =
          visibleTypeAlarmIds.value.filter(
            value =>
              isTypeAlarmSelected(value)
          ).length;

        return (
          selectedCount > 0 &&
          selectedCount <
            visibleTypeAlarmIds.value.length
        );
      });


    //Dynamic checkbox text
    const selectAllLabel = computed(() => {
      if (searchText.value.trim()) {
        return allVisibleSelected.value
          ? proxy.$t("deselect_matches") +
              "(" +
              visibleTypeAlarmIds.value.length +
              ")"
          : proxy.$t("select_matches") +
              "(" +
              visibleTypeAlarmIds.value.length +
              ")";
      }

      return allVisibleSelected.value
        ? proxy.$t("deselect_all") +
            "(" +
            visibleTypeAlarmIds.value.length +
            ")"
        : proxy.$t("select_all") +
            "(" +
            visibleTypeAlarmIds.value.length +
            ")";
    });

    //updates the searching text
    const onTypeAlarmSearch = search => {
      searchText.value = search || "";
    };

    const onTypeAlarmInput = value => {
      const selectedValues =
        Array.isArray(value)
          ? [...value]
          : [];

      emit("update:itemData", {
        type_alarm: [...selectedValues],
      });
    };

    //selects or deselects just visible options the options that are not showing in the current search and were selected stay selected
    const toggleVisibleOptions = checked => {
      const selectedMap = new Map();

      selectedTypeAlarmIds.value.forEach(
        value => {
          selectedMap.set(
            String(value),
            value
          );
        }
      );

      if (checked) {
        //Add coincidences without deleted selected types
        visibleTypeAlarmIds.value.forEach(
          value => {
            selectedMap.set(
              String(value),
              value
            );
          }
        );
      } else {
        //Deletes just coincidences
        visibleTypeAlarmIds.value.forEach(
          value => {
            selectedMap.delete(
              String(value)
            );
          }
        );
      }

      onTypeAlarmInput([
        ...selectedMap.values(),
      ]);
    };


    //
    onMounted(() => {
      store.dispatch('type_alarm_category/getAll', { max: 1000, enabled: true });
    });

    const typeAlarmCategoryOptions = computed(() => store.getters['type_alarm_category/getSelectedItems']);

    const toggleModal = () => {
      root.$bvModal.show("type-alarm-category-filters-modal");
    };

    const onChangeCategory = (typeAlarmCategory) => {
      if(!typeAlarmCategory) return;
      
      const { id_type_alarms } = typeAlarmCategory;

      idTypeAlarmsFiltered.value = id_type_alarms;
    };

    const onSelectFiltered = (isSelection) => {
      if(isSelection) {
        const typeAlarmsIds = [
          ...props.itemData.type_alarm,
          ...idTypeAlarmsFiltered.value,
        ];

        const uniqueTypeAlarmsIds = [...new Set(typeAlarmsIds)];

        emit("update:itemData", {
          type_alarm: uniqueTypeAlarmsIds,
        });
      } else {
        const typeAlarmsIds = props.itemData.type_alarm.filter(
          (id) => !idTypeAlarmsFiltered.value.includes(id)
        );

        emit("update:itemData", {
          type_alarm: typeAlarmsIds,
        });        
      }

      idTypeAlarmsFiltered.value = [];
      root.$bvModal.hide("type-alarm-category-filters-modal");
    }

    return {
      getValidationState,

      normalizedTypeAlarmOptions,

      searchText,
      onTypeAlarmSearch,
      filterTypeAlarmOptions,

      visibleTypeAlarmOptions,
      visibleTypeAlarmIds,

      isTypeAlarmSelected,

      allVisibleSelected,
      someVisibleSelected,
      selectAllLabel,
      toggleVisibleOptions,

      onTypeAlarmInput,

      // category filter
      toggleModal,
      onSelectFiltered,
      onChangeCategory,
      idTypeAlarmsFiltered,
      typeAlarmCategoryOptions,
    };
  },
};
</script>

<style lang="scss">
.type-alarm-option {
  display: flex;
  width: 100%;
  min-height: 28px;
  align-items: center;
  cursor: pointer;
}


.custom-option-checkbox {
  display: inline-flex;
  width: 17px;
  height: 17px;
  flex: 0 0 17px;
  align-items: center;
  justify-content: center;
  border: 1px solid #b9b9c3;
  border-radius: 3px;
  background-color: #fff;
  pointer-events: none;
}

.custom-option-checkbox-selected {
  color: #fff;
  border-color: #7367f0;
  background-color: #7367f0;
}

.vs__dropdown-option .type-alarm-option {
  width: 100%;
}
</style>